import React, {useEffect} from 'react';
import Grid from '../../../molecules/Grid';
import {Controller, FieldValues, Path, useFormContext} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import TestingFormRadio from '../TestingFormRadio/TestingFormRadio';
import TestingFormCheckbox from '../TestingFormCheckbox/TestingFormCheckbox';
import TestingFormTextField from '../TestingFormTextField/TestingFormTextField';
import {saveValidity, setFormValidity} from '@/store/reducers/testingSlice.js';
import TestingFormSelect from '../TestingFormSelect/TestingFormSelect';
import {TestingAnswersProps} from './types';

const TestingAnswersList = <T extends FieldValues, P extends Path<T>>({
        options,
        onAnswerClick,
        name,
   }: TestingAnswersProps<T, P>) => {

    const dispatch = useDispatch()
    const {
        control,
        reset,
        formState: {isValid  },
        setValue,
        trigger
    } = useFormContext();

    const {activeQuestion, answers, testData} =
        useSelector((state: any) => state.testing)
    const maxCheckbox: any = testData[activeQuestion]?.answers.validate?.max_checkbox;
    const [isComment, showComment] = React.useState(false);
    const [isDisabled, setDisabled] = React.useState(false);

    useEffect(() => {
        reset();
    }, [activeQuestion]);

    useEffect(() => {
        if (options) {
            const getDefaultValue = (opt, index) => {
                const val = opt.val || index;
                const activeQuestionAnswers = answers[activeQuestion]?.answer;

                const defaultMappings = {
                    radio: () => activeQuestionAnswers?.[0]?.val === val ? val : null,
                    text: () => activeQuestionAnswers?.find(a => a.name === val)?.val || '',
                    number: () => activeQuestionAnswers?.find(a => a.name === val)?.val || '',
                    select: () => activeQuestionAnswers?.find(a => a.name === val)?.val || null,
                    checkbox: () => activeQuestionAnswers?.some(answer => answer.name === val) || false,
                    default: () => null
                };

                const getDefaultValueForType = type => defaultMappings[type] ? defaultMappings[type]() : defaultMappings.default();

                return getDefaultValueForType(opt.type);
            };

            options.forEach((opt, index) => {
                const defaultValue = getDefaultValue(opt, index);
                setValue(`${name}_${activeQuestion}_${index}`, defaultValue);
            });
        }
    }, [activeQuestion, answers, options]);

    useEffect(() => {
        dispatch(setFormValidity(isValid))
        dispatch(saveValidity(isValid));
        trigger();
    }, [ isValid, activeQuestion, testData, options, answers]);

    useEffect(() => {
        //show comment when change active question
        setDisabled(false);
        showComment(false);

        if (options.some(item => item.hasOwnProperty('comment') && item.comment !== "")) {
            if (answers[activeQuestion]) {
                showComment(true);
            }
        }

        if (options.some(item => item.hasOwnProperty('is_correct'))) {
            if (answers[activeQuestion]) {
                setDisabled(true);
            }
        }
    }, [activeQuestion, answers, options]);

    const onRadioClicked = (value) => {
        onAnswerClick && onAnswerClick(value, 'radio');
    }

    // todo использовать функцию трансформации вынесенную в утилиты
    const transformSelectOptions = (items) => {
        return items.map((item) => ({
            value: item.val ? item.val : item.label,
            label: item.label,
            isDisabled: item.disabled,
            selected: item.selected,
        }));
    };

    return (
        <Grid col='1' gap='2'>
            {options.map((opt, index) => (
                <Controller
                    key={index}
                    name={`${name}_${activeQuestion}_${index}` as P}
                    control={control}
                    render={({
                                 field,
                                 field: {onChange, value },
                                 fieldState: { error, isDirty }
                    }) => {

                        let val = opt.val ? opt.val : index;
                        let radioChecked = val === answers[activeQuestion]?.answer[0].val;
                        let checkboxChecked = answers[activeQuestion]?.answer?.some(answer => answer.name === val);
                        let activeCheckboxesCount = answers[activeQuestion]?.answer?.filter(a => a.val === 1).length;
                        let existedAnswer = answers[activeQuestion]?.answer.find(a => a.name === val)?.val;
                        let fieldError;
                        if (existedAnswer && error || isDirty) {fieldError = error?.message}

                        const optionProps = {
                            type: opt.type,
                            name: field.name,
                            label: opt.label,
                            commentText: opt.comment,
                            imgSrc: opt.img,
                            error: fieldError,
                            required: opt.validate?.required,
                            value: value,
                        };


                        const selectOptions = opt.options ? transformSelectOptions(opt.options) : null;

                        const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                            onAnswerClick && onAnswerClick({ name: val, checked: e.target.checked }, opt.type);
                            onChange(e);
                        };

                        const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                            onAnswerClick && onAnswerClick({name: val, value: e.target.value}, opt.type);
                            onChange(e);
                        }

                        const handleSelectChange = (selectedOption) => {
                            const newValue = selectedOption ? selectedOption.value : '';
                            onAnswerClick && onAnswerClick({name: val, value: newValue}, opt.type);
                            onChange(newValue);
                        }

                        return (
                            <>
                                {(opt.type === 'select') &&
                                    <TestingFormSelect
                                        {...optionProps}
                                        options={selectOptions}
                                        placeholder={opt.placeholder}
                                        onChange={handleSelectChange}
                                        isComment={isComment}
                                        isCorrect={opt.is_correct}
                                        disabled={isDisabled}
                                        isClearable={true}
                                    />
                                }
                                {(opt.type === 'text') &&
                                    <TestingFormTextField
                                        {...optionProps}
                                        placeholder={opt.placeholder}
                                        onChange={handleFieldChange}
                                        isComment={isComment}
                                        isCorrect={opt.is_correct}
                                        minLength={opt.validate?.minLength}
                                        maxLength={opt.validate?.maxLength}
                                        disabled={isDisabled}
                                    />
                                }
                                {(opt.type === 'number') &&
                                    <TestingFormTextField
                                        {...optionProps}
                                        placeholder={opt.placeholder}
                                        onChange={handleFieldChange}
                                        isComment={isComment}
                                        isCorrect={opt.is_correct}
                                        step={opt.validate?.step}
                                        min={opt.validate?.min}
                                        max={opt.validate?.max}
                                        disabled={isDisabled}
                                    />
                                }
                                {(opt.type === 'radio') &&
                                    <TestingFormRadio
                                        {...optionProps}
                                        onChange={onChange}
                                        onAnswerClick={() => onRadioClicked(val)}
                                        checked={radioChecked}
                                        {...('is_correct' in opt && { isCorrect: opt.is_correct })}
                                        isComment={isComment}
                                        disabled={isDisabled}
                                    />
                                }
                                {(opt.type === 'checkbox') &&
                                    <TestingFormCheckbox
                                        {...optionProps}
                                        onChange={handleCheckboxChange}
                                        {...('is_correct' in opt && (activeCheckboxesCount >= maxCheckbox) && { isCorrect: opt.is_correct })}
                                        isComment={isComment && (activeCheckboxesCount >= maxCheckbox)}
                                        disabled={
                                            (!checkboxChecked && activeCheckboxesCount >= maxCheckbox) ||
                                            ((activeCheckboxesCount >= maxCheckbox) && isDisabled)
                                        }
                                    />
                                }
                            </>
                        );
                    }}
                />
            ))}

        </Grid>
    )
}

export default  TestingAnswersList

